import React, { type ReactNode } from "react";

import { CheckIcon } from "@heroicons/react/outline";

import clsx from "clsx";

import Heading from "components/heading";
import HeroSection from "components/hero-section";

const yourFocus = [
  "Fund structure and investor relations",
  "Product design (rates, terms, and credit policy)",
  "Capital deployment strategy",
  "Portfolio performance reporting to investors",
  "Operations oversight and risk management",
  "Regulatory and program-level compliance",
];

const enerfloFocus = [
  "Installer onboarding and in-home sales support tools",
  "Loan and TPO origination, documentation, and e-signing",
  "Fraud detection and risk management tooling",
  "Milestone tracking and project management",
  "Change orders and reconciliation",
  "Compliance, security, and audit infrastructure",
  "API integrations with your systems and partners",
  "Available free of charge to your installer network",
];

type FocusBlockProps = {
  tagline: ReactNode;
  heading: ReactNode;
  description: ReactNode;
  items: string[];
  scheme: "blue" | "green";
};

const FocusBlock = ({ tagline, heading, description, items, scheme }: FocusBlockProps) => (
  <section className={clsx("p-4 md:p-6 rounded-xl text-left", scheme === "blue" ? "bg-blue-100" : "bg-green-100")}>
    <p className={clsx("uppercase font-semibold text-sm", scheme === "blue" ? "text-primary" : "text-green-500")}>
      {tagline}
    </p>
    <Heading tag="h3" size="h5" className="mb-3">
      {heading}
    </Heading>
    <p className="text-gray-800 mb-6">{description}</p>
    <ul className="space-y-4">
      {items.map(item => (
        <li key={item} className="flex items-start gap-3 text-left">
          <div
            className={clsx(
              "inline-block p-1 rounded-full shadow-md text-white",
              scheme === "blue" ? "bg-primary" : "bg-green-400"
            )}
          >
            <CheckIcon className="h-4 w-4" />
          </div>
          <p className="text-gray-800">{item}</p>
        </li>
      ))}
    </ul>
  </section>
);

const title = "Solar Financing Partnership Model";

const PartnershipModel = () => (
  <div className="bg-gray-50">
    <HeroSection title={title}>
      <HeroSection.Content>
        <header className="text-center mb-12">
          <HeroSection.Tagline className="text-primary mb-4">{title}</HeroSection.Tagline>
          <Heading tag="h2" className="mb-6">
            <span>You Run the Program.</span>
            <br />
            <span className="text-primary inline-block mt-2">Enerflo Powers the Solar Financing Infrastructure.</span>
          </Heading>
          <p className="text-lg text-gray-600 max-w-5xl mx-auto mb-8">
            As a solar lender, TPO financier, or capital provider, your focus is your financial product and your
            investors. Enerflo provides the platform — the tools, workflows, and infrastructure your team uses to run
            origination, manage risk, and oversee operations in the field.
          </p>
        </header>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FocusBlock
            tagline="Your Focus"
            heading="Your Product. Your Investors. Your Portfolio."
            description="Stay focused on what you do best — the financial structure, your capital strategy, and your program’s
              performance."
            items={yourFocus}
            scheme="green"
          />

          <FocusBlock
            tagline="Enerflo’s Focus"
            heading="The Tools to Originate, Manage Risk, and Run Your Operations."
            description="Enerflo gives your team the infrastructure to run every operational layer — from the sales floor to
              post-install portfolio monitoring."
            items={enerfloFocus}
            scheme="blue"
          />
        </div>

        <div className="text-center mt-10">
          <a
            href="#get-started"
            className="btn w-full xs:w-auto bg-primary text-white !px-6 py-3 rounded-md inline-block"
          >
            Book a Discovery Call
          </a>
        </div>
      </HeroSection.Content>
    </HeroSection>
  </div>
);

export default PartnershipModel;
